import { get, isNil, set } from "lodash";
import {
  factorPriceCacheKey,
  readFactorPriceCache,
  writeFactorPriceCache,
} from "./factorPriceCache";

const ADVANCED_PREFIX = /^advanced\./;
const FACTOR_PRICE_PREFIX = /^price\./;
const FACTOR_BASE_PRICE_PREFIX = "basePrice.";

export const targetPricePath = (source) =>
  String(source).replace(ADVANCED_PREFIX, "");

export const applyPresentedPrice = (next, source, presented) => {
  if (!presented || isNil(presented.formatted)) return next;
  set(next, targetPricePath(source), presented.formatted);
  if (presented.baseFormatted) {
    set(
      next,
      targetPricePath(source).replace(
        FACTOR_PRICE_PREFIX,
        FACTOR_BASE_PRICE_PREFIX,
      ),
      presented.baseFormatted,
    );
  }
  return next;
};

export const readCachedPrices = ({
  projectId,
  insurancePlanId,
  partnerCode,
  pricedSlots,
}) => {
  const next = {};
  for (const slot of pricedSlots || []) {
    const key = factorPriceCacheKey({
      projectId,
      insurancePlanId,
      partnerCode,
      source: slot.binding.source,
      factors: slot.binding.factors,
    });
    const hit = readFactorPriceCache(key);
    if (!hit) return null;
    applyPresentedPrice(next, slot.binding.source, hit);
    if (hit.appliedDiscountCode && !next.appliedDiscountCode) {
      next.appliedDiscountCode = hit.appliedDiscountCode;
    }
  }
  return next;
};

export const nextDisplayedPrices = ({ cached, planChanged }) => {
  if (cached) return cached;
  if (planChanged) return {};
  return undefined;
};

export const collectPrefetchPlanIds = ({ selectedPlanId, extraIds = [] }) => {
  const ids = [];
  const seen = new Set();
  const selected = selectedPlanId ? String(selectedPlanId) : "";
  for (const raw of extraIds) {
    const id = raw == null ? "" : String(raw).trim();
    if (!id || id === selected || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export const selectedPlanOptionIds = (doc, slots) => {
  const ids = [];
  for (const slot of slots || []) {
    if (get(slot, "binding.field") !== "selectedPlan") continue;
    const root =
      doc && doc.querySelector("[data-kayros-slot='" + slot.id + "']");
    if (!root) continue;
    root.querySelectorAll("[data-value]").forEach((node) => {
      const value = node.getAttribute("data-value");
      if (value) ids.push(value);
    });
  }
  return ids;
};

export const storePrices = ({
  projectId,
  insurancePlanId,
  partnerCode,
  pricedSlots,
  payload,
}) => {
  const next = {};
  const prices = get(payload, "prices") || {};
  for (const slot of pricedSlots || []) {
    const source = slot.binding.source;
    const presented =
      prices[source] ||
      (pricedSlots.length === 1
        ? {
            formatted: payload.formatted,
            baseFormatted: payload.baseFormatted,
            appliedDiscountCode: payload.appliedDiscountCode,
          }
        : null);
    if (!presented || isNil(presented.formatted)) continue;
    applyPresentedPrice(next, source, presented);
    writeFactorPriceCache(
      factorPriceCacheKey({
        projectId,
        insurancePlanId,
        partnerCode,
        source,
        factors: slot.binding.factors,
      }),
      presented,
    );
  }
  if (payload && payload.appliedDiscountCode) {
    next.appliedDiscountCode = payload.appliedDiscountCode;
  }
  return next;
};
